/**
 * The platform console's past papers (/admin/papers) — CWY's own board papers,
 * set once for every school on a board.
 *
 * Every paper here is GLOBAL: published, it reaches every student on its board
 * and grade under Tests & papers → Past papers, and "Beat your best" once they
 * have sat it. A paper is added by uploading its PDF — AI reads it, solves it
 * and files every question under a sub-topic — or by typing it in; either way
 * it lands as a draft that is reviewed row by row before anyone sees it.
 *
 * A published paper is never deleted, because sittings and reports point at
 * it. Withdraw takes it off every list and stops new sittings; Restore puts it
 * back. Only a draft that never went live can be deleted.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  MAX_PDF_BYTES,
  gradeLabel,
  type CurriculumLevelsDTO,
  type CurriculumTreeDTO,
  type PlatformPaperDraftDTO,
  type PlatformPaperRow,
  type PlatformPaperState,
} from '@cwy/config/contracts';
import { apiDelete, apiErrorMessage, apiGet, apiPost, uploadToS3 } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  StudentPage,
  StudentPageHeader,
} from '@/components/ds';
import { QueryState } from '@/components/admin/QueryState';

const LIST_KEY = ['platform', 'papers'] as const;

type StatusFilter = 'all' | 'draft' | 'published' | 'withdrawn';

/** How each state reads, and the tone of its pill. */
export const PAPER_STATE: Record<PlatformPaperState, { text: string; tone: string }> = {
  draft: { text: 'Draft', tone: 'bg-paper-sunk text-ink' },
  processing: { text: 'Reading PDF…', tone: 'bg-amber-50 text-amber-700' },
  review: { text: 'Ready to review', tone: 'bg-indigo-50 text-indigo-700' },
  done: { text: 'Ready to review', tone: 'bg-indigo-50 text-indigo-700' },
  publishing: { text: 'Publishing…', tone: 'bg-amber-50 text-amber-700' },
  published: { text: 'Live', tone: 'bg-green-50 text-green-700' },
  withdrawn: { text: 'Withdrawn', tone: 'bg-slate-100 text-slate-600' },
  failed: { text: 'Could not read', tone: 'bg-red-50 text-red-700' },
};

const SOURCE_LABEL: Record<PlatformPaperRow['source'], string> = {
  SEED: 'Seeded',
  UPLOAD: 'From PDF',
  MANUAL: 'Typed in',
};

export function StatePill({ state }: { state: PlatformPaperState }) {
  const s = PAPER_STATE[state] ?? PAPER_STATE.draft;
  return (
    <span
      className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.tone}`}
    >
      {s.text}
    </span>
  );
}

/** Where a row opens: a draft's editor, or a published paper's. */
export const paperEditorPath = (row: Pick<PlatformPaperRow, 'kind' | 'id'>) =>
  row.kind === 'PAPER' ? `/admin/papers/${row.id}?kind=paper` : `/admin/papers/${row.id}`;

export function PlatformPapersPage() {
  const navigate = useNavigate();
  const [board, setBoard] = useState('');
  const [grade, setGrade] = useState('');
  const [year, setYear] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const levels = useQuery({
    queryKey: ['curriculum', 'levels'],
    queryFn: () => apiGet<CurriculumLevelsDTO>('/curriculum/levels'),
  });

  const search = new URLSearchParams({ status });
  if (board) search.set('board', board);
  if (grade) search.set('grade', grade);
  if (/^\d{4}$/.test(year)) search.set('year', year);
  const list = useQuery({
    queryKey: [...LIST_KEY, search.toString()],
    queryFn: () => apiGet<PlatformPaperRow[]>(`/platform/papers?${search.toString()}`),
    // A PDF being read finishes on its own; the list notices.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((row) => row.state === 'processing') ? 5_000 : false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: LIST_KEY });

  const withdraw = useMutation({
    mutationFn: ({ id, restore }: { id: string; restore: boolean }) =>
      apiPost(`/platform/papers/published/${id}/${restore ? 'restore' : 'withdraw'}`, {}),
    onSuccess: () => {
      setError('');
      void invalidate();
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/platform/papers/drafts/${id}`),
    onSuccess: () => {
      setError('');
      void invalidate();
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  const rows = list.data ?? [];

  return (
    <StudentPage>
      <StudentPageHeader
        title="Past papers"
        subtitle="Board papers for every school. Published, a paper reaches every student on its board and grade under Tests & papers."
        action={
          <Button onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : 'Add paper'}</Button>
        }
      />

      {adding && (
        <AddPaperForm
          levels={levels.data}
          onCreated={(id) => {
            void invalidate();
            navigate(`/admin/papers/${id}`);
          }}
        />
      )}

      <Card>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Board">
            {({ id }) => (
              <Select
                id={id}
                controlSize="sm"
                value={board}
                onChange={(e) => setBoard(e.target.value)}
              >
                <option value="">All boards</option>
                {levels.data?.boards.map((b) => (
                  <option key={b.code} value={b.code}>
                    {b.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Grade">
            {({ id }) => (
              <Select
                id={id}
                controlSize="sm"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
              >
                <option value="">All grades</option>
                {levels.data?.grades.map((g) => (
                  <option key={g.code} value={g.code}>
                    {g.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Year">
            {({ id }) => (
              <Input
                id={id}
                controlSize="sm"
                inputMode="numeric"
                placeholder="Any year"
                value={year}
                onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            )}
          </Field>
          <Field label="Status">
            {({ id }) => (
              <Select
                id={id}
                controlSize="sm"
                value={status}
                onChange={(e) => setStatus(e.target.value as StatusFilter)}
              >
                <option value="all">Everything</option>
                <option value="draft">Drafts</option>
                <option value="published">Live</option>
                <option value="withdrawn">Withdrawn</option>
              </Select>
            )}
          </Field>
        </div>
      </Card>

      {error && (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {error}
        </p>
      )}

      <QueryState
        isPending={list.isPending}
        error={list.error}
        isEmpty={rows.length === 0}
        empty={<p className="text-sm text-ink-soft">No papers match these filters.</p>}
      >
        <Card padding="0">
          <ul className="divide-y divide-line">
            {rows.map((row) => (
              <li
                key={`${row.kind}:${row.id}`}
                className="flex flex-wrap items-center gap-3 px-4 py-3"
              >
                <button
                  type="button"
                  onClick={() => navigate(paperEditorPath(row))}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="block truncate text-sm font-semibold text-ink">{row.title}</span>
                  <span className="mt-0.5 block text-xs text-ink-soft">
                    {[
                      row.subjectName,
                      `${row.board} · Class ${gradeLabel(row.grade)}`,
                      row.examYear,
                      `${row.questionCount} question${row.questionCount === 1 ? '' : 's'}`,
                      SOURCE_LABEL[row.source],
                      row.kind === 'PAPER' && row.sittings > 0
                        ? `${row.sittings} sitting${row.sittings === 1 ? '' : 's'}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {row.state === 'failed' && row.error && (
                    <span className="mt-1 block text-xs text-red-700">{row.error}</span>
                  )}
                </button>
                <StatePill state={row.state} />
                <div className="flex flex-none gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(paperEditorPath(row))}
                  >
                    Open
                  </Button>
                  {row.kind === 'PAPER' && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={withdraw.isPending}
                      onClick={() => {
                        const restore = row.state === 'withdrawn';
                        const ask = restore
                          ? `Restore “${row.title}”? It returns to every student’s past papers.`
                          : `Withdraw “${row.title}”? It leaves every student’s list and cannot be started. Sittings already made, and their reports, stay.`;
                        if (window.confirm(ask)) withdraw.mutate({ id: row.id, restore });
                      }}
                    >
                      {row.state === 'withdrawn' ? 'Restore' : 'Withdraw'}
                    </Button>
                  )}
                  {row.kind === 'DRAFT' && row.state !== 'publishing' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (
                          window.confirm(`Delete the draft “${row.title}”? This cannot be undone.`)
                        ) {
                          remove.mutate(row.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </QueryState>
    </StudentPage>
  );
}

/**
 * Add a paper: its cohort, subject and year, then either a PDF for AI to read
 * or a blank draft to type into. Board and grade come from the live tree, and
 * the subject from that cohort's syllabus — never typed.
 */
function AddPaperForm({
  levels,
  onCreated,
}: {
  levels: CurriculumLevelsDTO | undefined;
  onCreated: (draftId: string) => void;
}) {
  const [how, setHow] = useState<'upload' | 'manual'>('upload');
  const [cohort, setCohort] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');

  const [board, grade] = cohort ? cohort.split('|') : ['', ''];
  const tree = useQuery({
    queryKey: ['curriculum', 'tree', board, grade],
    enabled: Boolean(board && grade),
    queryFn: () => apiGet<CurriculumTreeDTO>(`/curriculum/tree?board=${board}&grade=${grade}`),
  });
  const subjects = useMemo(() => tree.data?.subjects ?? [], [tree.data]);
  const subject = subjects.find((s) => s.id === subjectId);
  const suggestedTitle =
    subject && board && grade && /^\d{4}$/.test(year)
      ? `${board} Class ${gradeLabel(grade)} ${subject.name} — ${year}`
      : '';

  const create = useMutation({
    mutationFn: async () => {
      const base = {
        title: (title.trim() || suggestedTitle).slice(0, 200),
        subjectId,
        examYear: Number(year),
      };
      if (how === 'manual') {
        return apiPost<PlatformPaperDraftDTO>('/platform/papers/drafts', base);
      }
      if (!file) throw new Error('Choose the PDF of the paper.');
      const { uploadUrl, s3Key } = await apiPost<{ uploadUrl: string; s3Key: string }>(
        '/platform/papers/presign',
        { fileName: file.name, contentType: 'application/pdf', sizeBytes: file.size },
      );
      setProgress(0);
      await uploadToS3(uploadUrl, file, 'application/pdf', setProgress);
      // Only the key goes back — the API checks it sits under this operator's prefix.
      return apiPost<PlatformPaperDraftDTO>('/platform/papers/drafts/from-pdf', {
        ...base,
        s3Key,
        fileName: file.name,
      });
    },
    onSuccess: (draft) => onCreated(draft.id),
    onError: (err) => {
      setProgress(null);
      setError(apiErrorMessage(err));
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!subjectId) return setError('Choose the board, grade and subject.');
    if (!/^\d{4}$/.test(year)) return setError('Give the year of the exam.');
    if (how === 'upload' && !file) return setError('Choose the PDF of the paper.');
    create.mutate();
  };

  return (
    <Card>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="How to add the paper">
          {(
            [
              ['upload', 'Upload a PDF'],
              ['manual', 'Type it in'],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              size="sm"
              variant={how === value ? 'primary' : 'secondary'}
              aria-pressed={how === value}
              onClick={() => setHow(value)}
            >
              {label}
            </Button>
          ))}
        </div>
        <p className="text-xs text-ink-soft">
          {how === 'upload'
            ? 'AI reads the paper, solves each question and files it under a sub-topic. Nothing is published until you have reviewed every row.'
            : 'Start an empty paper and add each question yourself — question, options, the correct choice, marks, hints and its sub-topic.'}
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Board and grade" required>
            {({ id }) => (
              <Select
                id={id}
                value={cohort}
                onChange={(e) => {
                  setCohort(e.target.value);
                  setSubjectId('');
                }}
              >
                <option value="">Choose…</option>
                {levels?.cohorts.map((c) => (
                  <option key={`${c.board}|${c.grade}`} value={`${c.board}|${c.grade}`}>
                    {levels.boards.find((b) => b.code === c.board)?.label ?? c.board} ·{' '}
                    {levels.grades.find((g) => g.code === c.grade)?.label ?? gradeLabel(c.grade)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Subject" required>
            {({ id }) => (
              <Select
                id={id}
                value={subjectId}
                disabled={!cohort || tree.isPending}
                onChange={(e) => setSubjectId(e.target.value)}
              >
                <option value="">{cohort ? 'Choose…' : 'Choose a board first'}</option>
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Exam year" required>
            {({ id }) => (
              <Input
                id={id}
                inputMode="numeric"
                value={year}
                onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            )}
          </Field>
        </div>

        <Field
          label="Title"
          hint={suggestedTitle ? `Leave blank for “${suggestedTitle}”.` : undefined}
        >
          {({ id }) => (
            <Input
              id={id}
              value={title}
              maxLength={200}
              placeholder={suggestedTitle || 'e.g. CBSE Class 10 Mathematics — 2025'}
              onChange={(e) => setTitle(e.target.value)}
            />
          )}
        </Field>

        {how === 'upload' && (
          <Field
            label="Question paper (PDF)"
            required
            hint={`Up to ${Math.floor(MAX_PDF_BYTES / 1024 / 1024)} MB.`}
          >
            {({ id }) => (
              <input
                id={id}
                type="file"
                accept="application/pdf,.pdf"
                className="text-sm"
                onChange={(e) => {
                  const chosen = e.target.files?.[0] ?? null;
                  if (chosen && chosen.size > MAX_PDF_BYTES) {
                    setError(
                      `That file is ${Math.ceil(chosen.size / 1024 / 1024)} MB — a paper can be at most ${Math.floor(MAX_PDF_BYTES / 1024 / 1024)} MB.`,
                    );
                    setFile(null);
                    return;
                  }
                  setError('');
                  setFile(chosen);
                }}
              />
            )}
          </Field>
        )}

        {error && (
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending
              ? progress !== null && progress < 100
                ? `Uploading… ${progress}%`
                : 'Starting…'
              : how === 'upload'
                ? 'Upload and read'
                : 'Start the paper'}
          </Button>
        </div>
      </form>
    </Card>
  );
}
